import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Mail, CheckCircle2, Clock } from 'lucide-react';
import { getContactInquiries, setInquiryResponded } from '../redux/contactInquirySlice';

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'notResponded', label: 'Not responded' },
  { id: 'responded', label: 'Responded' },
];

// Admin: custom-plan contact requests from doctors + the public landing page.
// Filter by tab and mark each responded once the team has reached out.
const ContactRequests = () => {
  const dispatch = useDispatch();
  const { inquiries, loading } = useSelector((s) => s.contactInquiry);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    dispatch(getContactInquiries());
  }, [dispatch]);

  const counts = {
    all: inquiries.length,
    notResponded: inquiries.filter((i) => !i.responded).length,
    responded: inquiries.filter((i) => i.responded).length,
  };
  const filtered = inquiries.filter((i) =>
    tab === 'all' ? true : tab === 'responded' ? i.responded : !i.responded
  );

  const toggle = (i) => dispatch(setInquiryResponded({ id: i.id, responded: !i.responded }));

  return (
    <div className="min-h-full w-full p-2" data-testid="contact-requests-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="p-2.5 rounded-2xl bg-[#4ABDBD]/10 text-[#4ABDBD] shadow-sm"><Mail className="w-6 h-6" /></span>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">Contact requests</h1>
          <p className="text-sm text-slate-500">Custom-plan enquiries from doctors and the public site.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex bg-gray-100 rounded-xl p-1 mb-5" data-testid="contact-requests-tabs">
        {TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            onClick={() => setTab(tb.id)}
            data-testid={`contact-tab-${tb.id}`}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${tab === tb.id ? 'bg-white text-[#2D8A8A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tb.label} <span className="ml-1 text-xs opacity-70">({counts[tb.id]})</span>
          </button>
        ))}
      </div>

      {loading && inquiries.length === 0 ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 py-10 text-center">No requests here.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((i) => (
            <div
              key={i.id}
              data-testid={`inquiry-${i.id}`}
              className={`rounded-2xl border bg-white shadow-sm p-4 sm:p-5 ${i.responded ? 'border-emerald-200' : 'border-gray-200'}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-[#1a365d]">{i.name}</span>
                    <a href={`mailto:${i.email}`} className="text-sm text-[#4ABDBD] hover:underline break-all">{i.email}</a>
                    <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{i.source}</span>
                  </div>
                  {i.packageName && <p className="text-xs text-slate-500 mt-0.5">Plan: <strong>{i.packageName}</strong></p>}
                  {i.notes && <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{i.notes}</p>}
                  <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(i.createdAt)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  data-testid={`mark-responded-${i.id}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${i.responded ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <CheckCircle2 className="w-4 h-4" /> {i.responded ? 'Responded' : 'Mark responded'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactRequests;
